import * as React from 'react';
import { useState } from 'react';

import { Link } from 'react-router';

import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { ChevronDown } from 'lucide-react';

import { TwPicture } from '@boc/images';

import { imgUrl } from '~/urls';

export interface MegaMenuImageLink {
  type: 'image';
  to: string;
  image: string;
  title: string;
}

export interface MegaMenuChildLinks {
  type: 'links';
  title: string;
  to?: string;
  children: {
    title: string;
    to: string;
  }[];
}

export interface MegaMenuParent {
  title: string;
  to: string;
  children?: (MegaMenuChildLinks | MegaMenuImageLink)[];
}

function MegaMenuChildLinks({
  child,
  onClose,
}: {
  child: MegaMenuChildLinks;
  onClose: () => void;
}) {
  return (
    <div>
      {child.to ? (
        <Link to={child.to} onClick={onClose}>
          <h3 className="text-medium leading-4 font-medium tracking-tight transition-colors duration-200">
            {child.title}
          </h3>
        </Link>
      ) : (
        <h3 className="text-medium leading-4 font-medium tracking-tight">{child.title}</h3>
      )}
      <div className="mt-6 flow-root">
        <div className="-my-2">
          {child.children.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              onClick={onClose}
              className="hover:text-primary-2-100 block py-1 text-base leading-6 font-semibold transition-all duration-200 hover:translate-x-1"
              // className="text-gray-900 flex gap-x-4 py-2 text-sm font-semibold leading-6"
            >
              {/* <item.icon className="text-gray-400 h-6 w-6 flex-none" aria-hidden="true" /> */}
              {item.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MegaMenuImageLink({ child, onClose }: { child: MegaMenuImageLink; onClose: () => void }) {
  return (
    <Link
      to={child.to}
      className="group block transition-transform duration-300 ease-out hover:scale-105"
      onClick={onClose}
    >
      <TwPicture
        imgUrl={imgUrl}
        src={child.image}
        defaultSize={200}
        className="border-primary-2-40 w-full rounded-lg border object-cover transition-all duration-300 ease-out group-hover:shadow-lg"
      />
      <h3 className="group-hover:text-primary-2-100 py-2 text-center text-base leading-6 font-semibold transition-colors duration-200">
        {child.title}
      </h3>
    </Link>
  );
}

function MegaMenuChild({
  child,
  onClose,
}: {
  child: MegaMenuChildLinks | MegaMenuImageLink;
  onClose: () => void;
}) {
  if (child.type == 'image') {
    return <MegaMenuImageLink child={child} onClose={onClose} />;
  }

  if (child.type == 'links') {
    return <MegaMenuChildLinks child={child} onClose={onClose} />;
  }

  return null;
}

function MegaMenuParent({ parent, onClose }: { parent: MegaMenuParent; onClose: () => void }) {
  if (!parent.children)
    return (
      <NavigationMenuPrimitive.Item>
        <NavigationMenuPrimitive.Link asChild>
          <Link
            to={parent.to}
            className="font-heading hover:text-primary-2-100 py-3 text-base leading-4 font-medium tracking-tight transition-colors duration-200"
          >
            {parent.title}
          </Link>
        </NavigationMenuPrimitive.Link>
      </NavigationMenuPrimitive.Item>
    );

  return (
    <NavigationMenuPrimitive.Item>
      <NavigationMenuPrimitive.Trigger className="font-heading group inline-flex h-auto items-center gap-x-1 bg-transparent py-3 text-base leading-4 font-medium tracking-tight transition-colors duration-200 hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent">
        {parent.title}
        <ChevronDown
          className="relative top-[1px] ml-1 h-3 w-3 transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-180"
          aria-hidden="true"
        />
      </NavigationMenuPrimitive.Trigger>
      <NavigationMenuPrimitive.Content className="bg-primary-2-10 data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion^=from-]:slide-in-from-top-2 data-[motion^=to-]:slide-out-to-top-2 data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 absolute inset-x-0 top-0 left-0 w-full shadow-lg ring-1 ring-gray-900/5">
        <div className="mx-auto grid max-w-screen-lg grid-cols-3 gap-x-8 gap-y-10 px-6 py-10 lg:grid-cols-5 lg:px-8">
          {parent.children.map((c) => {
            return <MegaMenuChild key={c.title} child={c} onClose={onClose} />;
          })}
        </div>
      </NavigationMenuPrimitive.Content>
    </NavigationMenuPrimitive.Item>
  );
}

export default function MegaMenu({ menu }: { menu: MegaMenuParent[] }) {
  const [currentValue, setCurrentValue] = useState<string>('');

  const handleClose = () => {
    setCurrentValue('');
  };

  return (
    <div className="text-primary-2-80 relative isolate z-40 hidden shadow md:flex md:justify-center">
      <NavigationMenuPrimitive.Root value={currentValue} onValueChange={setCurrentValue}>
        <NavigationMenuPrimitive.List className="group flex flex-1 list-none items-center justify-center gap-6">
          {menu.map((mp) => {
            return <MegaMenuParent key={mp.title} parent={mp} onClose={handleClose} />;
          })}
        </NavigationMenuPrimitive.List>
        <div className="absolute top-full left-0 flex w-full justify-center perspective-[2000px]">
          <NavigationMenuPrimitive.Viewport className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full origin-top overflow-hidden rounded-md border-0 bg-transparent transition-[width,height] duration-300 ease-in-out" />
        </div>
      </NavigationMenuPrimitive.Root>
    </div>
  );
}
